import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Workspace } from '@workspace/domain/workspace';
import { SidebarTreeWorkspaceItemContainer } from './SidebarTreeWorkspaceItemContainer';
import type { SidebarTabTarget } from '../SidebarTabContainer';
import { useAtomValue } from 'jotai';
import { controlAtom } from './SidebarTreeStore/sidebarTreeControlState';
import { workspacesAtom, folderTreesAtom } from './SidebarTreeStore/sidebarTreeRecordsState';
import { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import type { GroupId } from 'balus-common';
import { useCurrentUserId } from '@framework/auth';
import { CreateWorkspaceButton } from './CreateWorkspaceButton';

type Props = {
  target: SidebarTabTarget;
  groupId: GroupId;
  isGroupAdminUser: boolean;
};

export const SidebarTree: React.FC<Props> = ({ target, groupId, isGroupAdminUser }: Props) => {
  const { t } = useTranslation();
  const workspaces = useAtomValue(workspacesAtom);
  const folderTrees = useAtomValue(folderTreesAtom);
  const { scrolledWorkspaceId } = useAtomValue(controlAtom);
  const currentUserId = useCurrentUserId();

  const sortedWorkspaces = useMemo(() => {
    const workspaceList = Object.values(workspaces);

    return workspaceList
      .filter((ws) => {
        if (target === 'Shared') return ws.isShared;
        if (target === 'Personal') return ws.isPersonal;
        return false; // unreachable
      })
      .sort(Workspace.compare);
  }, [target, workspaces]);

  if (target === 'Bookmark') return null;
  if (currentUserId === null) return null;

  if (sortedWorkspaces.length <= 0) {
    if (isGroupAdminUser) {
      return (
        <div className="p-2">
          <CreateWorkspaceButton groupId={groupId} userId={currentUserId} />
        </div>
      );
    }

    return <div className="mx-2 mt-4">{t('sidebar.tree.noWorkspace')}</div>;
  }

  return (
    <div>
      {sortedWorkspaces.map((workspace) => {
        const rootFolderTree = folderTrees[workspace.id] || RootFolderTree.buildEmpty();
        return (
          <SidebarTreeWorkspaceItemContainer
            key={workspace.id}
            workspace={workspace}
            rootFolderTree={rootFolderTree}
            shouldBeScrolled={scrolledWorkspaceId === workspace.id}
          />
        );
      })}
    </div>
  );
};
